import React from 'react';
import { Link } from 'react-router-dom';
import {
  CalendarDays,
  Camera,
  CheckCircle2,
  Download,
  FileText,
  Globe,
  Map,
  Plane,
  ShieldCheck,
  Sparkles,
} from 'lucide-react';
import Footer from '../shared/Footer';
import cookieManager from '../../utils/cookieManager';

const usePageTitle = (title) => {
  React.useEffect(() => {
    if (title) document.title = title;
  }, [title]);
};

const highlights = [
  {
    title: '隐私安全',
    desc: '不存储用户数据，表单信息仅用于实时填写，完成后自动清除，保护您的隐私安全。',
    icon: ShieldCheck,
  },
  {
    title: '中文界面',
    desc: '完全中文化操作，对照越南官网英文表单逐字段翻译，不会英文也能轻松办理。',
    icon: Globe,
  },
  {
    title: 'AI 自动填表',
    desc: 'AI 智能填表，自动完成越南官网所有字段，准确率 98%，节省时间。',
    icon: Sparkles,
  },
  {
    title: '流程优化',
    desc: '优化填写流程，复杂字段提供示例和提示，减少填写错误和被退回风险。',
    icon: Map,
  },
  {
    title: '实时进度',
    desc: '实时显示自动化进度（个人信息→护照→行程...），验证码出现时桌面通知。',
    icon: CalendarDays,
  },
  {
    title: '贴心服务',
    desc: '自动化失败积分退还；疑难场景（多次入境、同行儿童）提供咨询支持。',
    icon: CheckCircle2,
  },
];

const steps = [
  '准备护照、头像、行程与住宿证明，确认护照有效期 6 个月以上',
  '在我们的中文界面填写 8 大版块信息（个人、行程、职业等），上传照片',
  '点击提交，AI 自动登录越南官网填写所有表单字段，耗时约 5-10 分钟',
  '系统提取验证码图片，您在页面上填写验证码，提交后继续自动化',
  '机器人自动跳转支付页面，您按提示支付 25/50 USD 后等待审批（3-5工作日）',
  '下载获批 e-Visa PDF，打印两份用于入境核验',
];

const faqList = [
  {
    q: '越南 e-Visa 办理周期多久？',
    a: '官方标称 3-5 个工作日，旺季可能延长至 5 个工作日。',
  },
  {
    q: '需要准备哪些材料？',
    a: '护照信息页扫描件、近 6 个月彩色白底半身照（4x6cm、<2MB JPG）、在越停留的酒店/联系地址、出入境日期与口岸。',
  },
  {
    q: '提交后还能修改吗？',
    a: '自动化开始后无法中止或修改。建议在点击"提交并进入自动化"前，仔细核对所有信息。如需修改，需等待本次完成后重新创建申请。',
  },
  {
    q: '什么是自动化填表？和手动填写有什么区别？',
    a: '您在我们的中文界面填写信息后，AI 机器人会自动登录越南移民局官网，按照您填写的内容逐字段填入英文表单。相比手动填写：节省时间（5-10分钟 vs 30-60分钟）、降低错误（机器人不会填错字段）、避免卡顿（无需应对官网偶尔的加载慢问题）。',
  },
  {
    q: '为什么需要我填写验证码？',
    a: '越南官网在表单提交前会出现验证码（Security Code），这是防机器人措施。我们的系统会截取验证码图片显示给您，您输入后系统继续自动化。整个过程需要您在线等待 5-10 分钟。',
  },
  {
    q: '收费多少？',
    a: '单次入境需要 200 积分，多次入境需要 400 积分。',
  },
  {
    q: '自动化失败怎么办？',
    a: '如果自动化过程中出现异常（网络问题、官网维护等），系统会标记失败状态。积分会自动退还到您的账户，您可以稍后重新提交，如多次失败可联系客服人工处理。',
  },
];

const VnVisaHome = () => {
  usePageTitle('旅报报·越南 e-Visa 助手 | AI 自动填表 · 5分钟搞定 · 积分计费');
  const isLoggedIn = cookieManager.isLoggedIn();
  const heroCtaPath = isLoggedIn ? '/vn-evisa' : '/auth/register';
  const heroCtaLabel = isLoggedIn ? '开始办理' : '注册并获取积分';
  const bottomCtaLabel = isLoggedIn ? '前往 e-Visa 表单' : '注册并获取积分';

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-lime-50 to-white">
      <header className="sticky top-0 z-40 backdrop-blur bg-white/70 border-b border-white/40">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="旅报报 logo" className="w-9 h-9" />
            <div className="font-extrabold text-gray-900">旅报报·越南签证助手</div>
          </Link>
          <div className="flex items-center gap-3">
            <a
              href="https://lvbaobao.space/"
              className="inline-flex items-center px-3 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm font-medium hover:border-emerald-500 hover:text-emerald-600 hover:bg-white transition-all"
            >
              <span className="sm:hidden">返回主站</span>
              <span className="hidden sm:inline">返回 lvbaobao.space</span>
            </a>
            <nav className="hidden md:flex items-center gap-5 text-sm text-gray-700">
              <a href="#features" className="hover:text-gray-900">产品能力</a>
              <a href="#flow" className="hover:text-gray-900">办理流程</a>
              <a href="#faq" className="hover:text-gray-900">FAQ</a>
              {isLoggedIn ? (
                <Link to="/home" className="px-4 py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-lime-500 text-white font-medium hover:from-emerald-600 hover:to-lime-600 transition-all">
                  个人中心
                </Link>
              ) : (
                <Link to="/auth/login" className="px-4 py-2 rounded-lg border-2 border-gray-300 text-gray-700 font-medium hover:border-emerald-500 hover:text-emerald-600 transition-all">
                  登录
                </Link>
              )}
            </nav>
          </div>
        </div>
      </header>

      <main>
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 opacity-70">
            <div className="absolute -top-20 -left-10 w-80 h-80 bg-emerald-200 blur-[120px]" />
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-lime-200 blur-[120px]" />
          </div>
          <div className="relative max-w-6xl mx-auto px-4 py-14 grid md:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center px-3 py-1 rounded-full bg-gradient-to-r from-emerald-500 to-lime-500 text-white text-xs font-medium mb-4">
                <Sparkles className="w-4 h-4 mr-2" /> AI + 专家双引擎
              </div>
              <h1 className="text-4xl md:text-6xl font-extrabold text-gray-900 leading-tight">
                越南 e-Visa，AI 自动化填表服务
              </h1>
              <p className="mt-5 text-gray-700 text-lg md:text-xl">
                AI 自动化技术，替您完成越南官网 8 大版块表单填写。填写一次，机器人帮你提交，您只需等待验证码，5-10分钟搞定。
              </p>
              <div className="mt-6 grid grid-cols-2 gap-3 max-w-md">
                <a
                  href="#features"
                  className="group inline-flex items-center justify-center px-4 py-3 rounded-2xl bg-gradient-to-r from-emerald-500 to-lime-500 text-white border-2 border-emerald-400 hover:scale-105 hover:shadow-lg transition-all duration-300 font-semibold text-sm md:text-base"
                >
                  了解关键能力
                </a>
                <a
                  href="#flow"
                  className="group inline-flex items-center justify-center px-4 py-3 rounded-2xl bg-white text-gray-900 border-2 border-gray-200 hover:border-emerald-400 hover:scale-105 hover:shadow-lg transition-all duration-300 font-medium text-sm md:text-base"
                >
                  查看办理流程
                </a>
                <Link
                  to={heroCtaPath}
                  className="group inline-flex items-center justify-center px-4 py-3 rounded-2xl bg-white/80 backdrop-blur text-emerald-700 border-2 border-emerald-100 hover:border-emerald-400 hover:bg-white hover:scale-105 transition-all duration-300 font-medium text-sm md:text-base"
                >
                  {heroCtaLabel}
                </Link>
                <Link
                  to="/vn-evisa-download"
                  className="group inline-flex items-center justify-center px-4 py-3 rounded-2xl bg-lime-50 text-lime-700 border-2 border-lime-200 hover:border-lime-400 hover:bg-lime-100 hover:scale-105 transition-all duration-300 font-medium text-sm md:text-base"
                >
                  <Download className="w-5 h-5 mr-2" />
                  E-VISA 下载
                </Link>
              </div>
            </div>
            <div className="relative">
              <div className="h-80 md:h-96 rounded-[32px] bg-white/90 border border-emerald-100 shadow-[0_20px_60px_-10px_rgba(16,185,129,0.3)] p-6 backdrop-blur">
                <div className="grid grid-cols-2 gap-6 h-full">
                  <div className="rounded-3xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 p-5 flex flex-col justify-between hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                    <div>
                      <p className="text-sm text-emerald-700 font-semibold">填表耗时</p>
                      <p className="mt-2 text-3xl font-black text-gray-900">5-10</p>
                      <p className="text-sm text-gray-500">分钟</p>
                    </div>
                    <p className="text-xs text-gray-500">AI 自动填写越南官网 8 大版块</p>
                  </div>
                  <div className="rounded-3xl bg-gradient-to-br from-lime-50 to-white border border-lime-100 p-5 flex flex-col justify-between hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                    <div>
                      <p className="text-sm text-lime-700 font-semibold">自动化成功率</p>
                      <p className="mt-2 text-3xl font-black text-gray-900">98%</p>
                      <p className="text-sm text-gray-500">已验证</p>
                    </div>
                    <p className="text-xs text-gray-500">累计完成多次自动化填表</p>
                  </div>
                  <div className="rounded-3xl bg-gradient-to-br from-white to-emerald-50 border border-emerald-100 p-5 flex flex-col justify-between hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                    <div>
                      <p className="text-sm text-emerald-700 font-semibold">支持场景</p>
                      <p className="mt-2 text-lg font-bold text-gray-900">旅行 / 商务 / 多次入境</p>
                    </div>
                    <p className="text-xs text-gray-500">覆盖 33 个口岸，含胡志明/河内等机场</p>
                  </div>
                  <div className="rounded-3xl bg-gradient-to-br from-white to-lime-50 border border-lime-100 p-5 flex flex-col justify-between hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                    <div>
                      <p className="text-sm text-lime-700 font-semibold">积分计费</p>
                      <p className="mt-2 text-3xl font-black text-gray-900">200/400</p>
                      <p className="text-sm text-gray-500">积分/次</p>
                    </div>
                    <p className="text-xs text-gray-500">单次入境 200 积分，多次入境 400 积分</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 签证类型对比区域 */}
        <section className="py-12 bg-gradient-to-b from-white to-emerald-50">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4">
                三种入境方式，哪种最适合你？
              </h2>
              <p className="text-gray-600 text-lg">
                批件、落地签、e-Visa 对比，一目了然
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {/* 批件 */}
              <div className="bg-white rounded-3xl border-2 border-gray-200 p-6 shadow-sm relative">
                <div className="absolute -top-3 left-6">
                  <span className="bg-gray-500 text-white text-xs px-3 py-1 rounded-full">传统方式</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mt-2 mb-4">批件（批文）</h3>
                <ul className="space-y-3 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <span className="text-red-500 mt-0.5">✗</span>
                    <span>需要通过旅行社申请</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-500 mt-0.5">✗</span>
                    <span>需先在入境海关办理落地签，然后再排队入关</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-500 mt-0.5">✗</span>
                    <span>办理落地签 + 排队入关，总耗时 1-2 小时</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500 mt-0.5">~</span>
                    <span>费用：约 30-50 USD + 旅行社服务费</span>
                  </li>
                </ul>
                <div className="mt-6 pt-4 border-t border-gray-100">
                  <p className="text-gray-500 text-sm">适合：团队游、旅行社代办</p>
                </div>
              </div>

              {/* 落地签 */}
              <div className="bg-white rounded-3xl border-2 border-gray-200 p-6 shadow-sm relative">
                <div className="absolute -top-3 left-6">
                  <span className="bg-amber-500 text-white text-xs px-3 py-1 rounded-full">需排队</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mt-2 mb-4">落地签</h3>
                <ul className="space-y-3 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <span className="text-red-500 mt-0.5">✗</span>
                    <span>到达机场后现场排队办理</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-500 mt-0.5">✗</span>
                    <span>高峰期排队 1-2 小时</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-500 mt-0.5">✗</span>
                    <span>需准备现金支付签证费</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500 mt-0.5">~</span>
                    <span>费用：25 USD（单次）+ 批文费</span>
                  </li>
                </ul>
                <div className="mt-6 pt-4 border-t border-gray-100">
                  <p className="text-gray-500 text-sm">适合：临时出行、无网络申请条件</p>
                </div>
              </div>

              {/* e-Visa - 推荐 */}
              <div className="bg-gradient-to-br from-emerald-50 to-lime-50 rounded-3xl border-2 border-emerald-400 p-6 shadow-lg relative transform hover:scale-[1.02] transition-transform">
                <div className="absolute -top-3 left-6">
                  <span className="bg-gradient-to-r from-emerald-500 to-lime-500 text-white text-xs px-3 py-1 rounded-full font-semibold">推荐 · 最省事</span>
                </div>
                <h3 className="text-xl font-bold text-emerald-700 mt-2 mb-4">e-Visa 电子签</h3>
                <ul className="space-y-3 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-emerald-500 mt-0.5">✓</span>
                    <span><strong>出发前在线办好</strong>，打印即可</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-emerald-500 mt-0.5">✓</span>
                    <span><strong>入境直接走 e-Visa 通道</strong>，无需排队</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-emerald-500 mt-0.5">✓</span>
                    <span>3-5 工作日出签，可加急</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-emerald-500 mt-0.5">✓</span>
                    <span>费用透明：25 USD（单次）/ 50 USD（多次）</span>
                  </li>
                </ul>
                <div className="mt-6 pt-4 border-t border-emerald-200">
                  <p className="text-emerald-700 text-sm font-medium">适合：自由行、商务出行、追求效率</p>
                </div>
              </div>
            </div>

            <div className="mt-8 text-center">
              <p className="text-gray-500 text-sm">
                * e-Visa 入境时走电子签证专用通道，通常 5 分钟内完成通关
              </p>
            </div>
          </div>
        </section>

        <section id="features" className="py-16 bg-white">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4">核心能力</h2>
              <p className="text-gray-600 text-lg">对标越南 e-Visa 资料页真实结构打造，让自助办理更轻松</p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {highlights.map((item, idx) => (
                <div
                  key={idx}
                  className="group bg-gradient-to-br from-white to-emerald-50 rounded-3xl border border-emerald-100/80 p-6 shadow hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-emerald-100 text-emerald-700 mb-4">
                    <item.icon className="w-6 h-6" />
                  </div>
                  <div className="font-bold text-gray-900 text-lg mb-2">{item.title}</div>
                  <div className="text-gray-600 text-sm leading-relaxed">{item.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="flow" className="py-16 bg-gradient-to-b from-emerald-50 to-white">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4">办理流程</h2>
              <p className="text-gray-600 text-lg">实测流程 + AI 提醒，减少重复输入与被退回的风险</p>
            </div>
            <ol className="space-y-4">
              {steps.map((text, idx) => (
                <li
                  key={idx}
                  className="group bg-white rounded-3xl border-2 border-emerald-100 p-5 shadow-sm hover:shadow-lg transition-all duration-300"
                >
                  <div className="flex items-start gap-4">
                    <span className="w-10 h-10 inline-flex items-center justify-center rounded-2xl bg-emerald-500 text-white font-bold text-lg group-hover:scale-105 transition-transform">
                      {idx + 1}
                    </span>
                    <p className="text-gray-700 leading-relaxed">{text}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section id="faq" className="py-16 bg-gradient-to-b from-emerald-50 to-white">
          <div className="max-w-5xl mx-auto px-4">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4">常见问题</h2>
              <p className="text-gray-600 text-lg">整理勘探报告中出现的高频疑问，让准备工作更从容</p>
            </div>
            <div className="space-y-4">
              {faqList.map((item, idx) => (
                <details key={idx} className="group bg-white border border-emerald-100 rounded-2xl p-6 shadow-sm">
                  <summary className="cursor-pointer font-semibold text-gray-900 flex items-center justify-between">
                    {item.q}
                    <span className="text-emerald-500 group-open:rotate-45 transition-transform">+</span>
                  </summary>
                  <p className="mt-3 text-gray-600 text-sm leading-relaxed">{item.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <div className="inline-flex items-center px-3 py-1 rounded-full bg-gradient-to-r from-emerald-500 to-lime-500 text-white text-xs font-medium mb-4">
              <Sparkles className="w-4 h-4 mr-2" /> 自动化服务已上线
            </div>
            <h2 className="text-3xl font-extrabold text-gray-900 mb-4">立即开始自动化填表</h2>
            <p className="text-gray-600 mb-6">
              越南 e-Visa 自动化服务已上线，注册账户即可使用，新用户赠送积分。
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Link
                to={heroCtaPath}
                className="inline-flex items-center justify-center px-6 py-4 rounded-2xl bg-gradient-to-r from-emerald-500 to-lime-500 text-white border border-emerald-400 hover:scale-105 hover:shadow-lg transition-all duration-300 font-semibold"
              >
                {bottomCtaLabel}
              </Link>
              <a
                href="mailto:lvbaobao@hongyi.space"
                className="inline-flex items-center justify-center px-6 py-4 rounded-2xl bg-white text-gray-900 border-2 border-gray-200 hover:border-emerald-400 hover:scale-105 hover:shadow-lg transition-all duration-300 font-medium"
              >
                咨询顾问
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer className="bg-emerald-50" />
    </div>
  );
};

export default VnVisaHome;
